/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 11:29:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { Button, Icon, Tooltip } from '@shopify/polaris';
import {
  QuestionMarkMinor,
  SectionMajor,
  AppsMajor,
  ColorsMajor,
  SettingsMajor
} from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import { change, getFormValues, reset } from 'redux-form';
// import Classnames from 'classnames';

import MenuItem from 'src/components/views/theme-settings/collapse/menu-item';
import MenuItemHasSub from 'src/components/views/theme-settings/collapse/menu-item-has-sub';
import ConfirmResetModal from 'src/components/views/theme-settings/confirm-reset-modal';
import IconSync from 'src/assets/images/common/sync.svg?react';

import { PAGES, TYPE_UPDATE_SETTING, SIDEBAR_MENU, DEVICE } from 'src/constants/theme-settings';
import { flattenMenu } from 'src/helpers/theme-settings-helper';
import * as ThemeSettingAction from 'src/stores/actions/theme-settings-actions';
import { trackFSEvent } from 'src/integrations/fullStory';

type Props = {
  formValues: any,
  activeMenuItem: string,
  selectedPage: { label: string, value: string },
  menuCommonSetting: any[],
  menuCollectionSetting: any[],
  menuSearchSetting: any[],
  menuSettingWithTitle: any[],
  isDifferentFormOrigin: boolean,
  updateField: (field, value) => void,
  changeActiveMenuItem: (id: string, updateType: string) => void,
  toggleHideMenuItem: (id: string, updateType: string) => void,
  upsertElementActions: (type: string, properties: Record<string, any>) => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  isShowModalReset: boolean
};

class SettingsVisualEditor extends Component<ComposedProps, State> {
  state = {
    isShowModalReset: false
  };

  menuSidebars = [
    {
      icon: SectionMajor,
      tab: SIDEBAR_MENU.SECTION,
      tooltip: this.props.i18n.translate('AdminHTML.ThemeSetting.Common.blocks')
    },
    {
      icon: AppsMajor,
      tab: SIDEBAR_MENU.APPS,
      tooltip: this.props.i18n.translate('AdminHTML.ThemeSetting.Common.components')
    },
    {
      icon: ColorsMajor,
      tab: SIDEBAR_MENU.COLOR,
      tooltip: this.props.i18n.translate('AdminHTML.ThemeSetting.Common.globalStyles')
    },
    {
      icon: SettingsMajor,
      tab: SIDEBAR_MENU.SETTING,
      tooltip: this.props.i18n.translate('AdminHTML.ThemeSetting.Common.oldVersions')
    }
  ];

  handleChangeActiveMenuItem = (id: string, updateType: string) => {
    const { changeActiveMenuItem } = this.props;
    changeActiveMenuItem(id, updateType);
  };

  handleToggleModalReset = (value: boolean) => {
    this.setState({
      isShowModalReset: value
    });
  };

  handleCancelReset = () => {
    this.handleToggleModalReset(false);
  };

  handleReset = () => {
    const {
      activeMenuItem,
      updateThemeSetting,
      themeSettingDefaultThemeValue,
      themeSettingDefaultValueOriginal,
      updateMultiFieldsReducer,
      themeName,
      resetForm
    } = this.props;

    let resetValue = {};
    if (!_.isEmpty(themeSettingDefaultValueOriginal?.valueByMenu)) {
      resetValue = themeSettingDefaultValueOriginal?.valueByMenu;
    } else {
      resetValue = themeSettingDefaultThemeValue?.valueByMenu;
    }

    resetForm();
    updateThemeSetting(resetValue);

    const payload = {
      isFormReplaceDirty: true,
      themeSettingDefaultValue: themeSettingDefaultValueOriginal,
      themeLibTemplate: '',
      originThemeLibTemplate: '',
      themeSettingByName: null,
      themeSettingByNameLoading: false,
      themeSettingByNameSuccess: false,
      themeSettingByNameError: null
    };

    // Reset activeMenuItem when is active and reset setting without activeMenuItem in that setting
    if (!(activeMenuItem in resetValue)) {
      payload.activeMenuItem = '';
      payload.activeMenuItemDetail = null;
    }

    updateMultiFieldsReducer(payload);
    this.handleToggleModalReset(false);

    // Track Full Story event
    trackFSEvent('Reset all settings', {
      feature: 'Visual Editor',
      selectedTheme: themeName
    });
  };

  handleUpdateMenuSetting = (id: string, children: any[], updateType: string) => {
    const { handleUpdateMenuSetting } = this.props;
    handleUpdateMenuSetting(id, children, updateType);
  };

  handleChangeSidebar = item => {
    const { updateFieldReducer } = this.props;
    updateFieldReducer('sideBarMenu', item.tab);
  };

  render() {
    const {
      i18n,
      activeMenuItem,
      selectedPage,
      menuCommonSetting,
      menuCollectionSetting,
      menuSearchSetting,
      toggleHideMenuItem,
      updateField,
      formValues,
      isDifferentFormOrigin,
      themeSettingDefaultValue,
      themeSettingDefaultThemeValue,
      themeSettingDefaultValueOriginal,
      themeName,
      upsertElementActions,
      removeCurrentElement,
      updateFieldReducer,
      // sideBarMenu,
      isExpandFullscreen
    } = this.props;
    const { isShowModalReset } = this.state;
    const menuSettingByPage =
      selectedPage?.value === PAGES.COLLECTION ? menuCollectionSetting : menuSearchSetting;
    const flattenCommonMenu = flattenMenu(menuCommonSetting);

    const leftPanelContainerClass = classnames('visual-editor-left-panel-container', {
      opened: !isExpandFullscreen
    });
    return (
      <div className={leftPanelContainerClass}>
        {/* <div className="visual-editor-sidebar">
          {this.menuSidebars.map((item, index) => (
            <Tooltip content={item.tooltip} key={index}>
              <div
                className={Classnames('visual-editor-sidebar__item', {
                  active: item.tab === sideBarMenu
                })}
                onClick={() => this.handleChangeSidebar(item)}>
                <Icon source={item.icon} color={item.tab === sideBarMenu ? 'primary' : 'base'} />
              </div>
            </Tooltip>
          ))}
        </div> */}
        <div className="visual-editor-settings">
          <div className="inner">
            <h2 className="title">{selectedPage?.label || ''}</h2>
            <form>
              <div className="menu-wrapper">
                {menuSettingByPage.map(item => (
                  <MenuItem
                    {...item}
                    key={item.id}
                    activeMenuItem={activeMenuItem}
                    updateType={
                      selectedPage?.value === PAGES.COLLECTION
                        ? TYPE_UPDATE_SETTING.COLLECTION
                        : TYPE_UPDATE_SETTING.SEARCH
                    }
                    formValues={formValues}
                    menus={flattenCommonMenu}
                    updateField={updateField}
                    handleChangeActiveMenuItem={this.handleChangeActiveMenuItem}
                    handleUpdateMenuSetting={this.handleUpdateMenuSetting}
                    upsertElementActions={upsertElementActions}
                    handleToggleHideMenuItem={toggleHideMenuItem}
                    themeSettingDefaultValue={themeSettingDefaultValue?.value}
                    themeSettingDefaultThemeValue={themeSettingDefaultThemeValue?.value}
                    themeSettingDefaultValueOriginal={themeSettingDefaultValueOriginal?.value}
                    themeName={themeName}
                    removeCurrentElement={removeCurrentElement}
                    updateFieldReducer={updateFieldReducer}
                  />
                ))}

                <div className="theme-setting-menu-divider-section">
                  <div className="divider-title">
                    <span>{i18n.translate('AdminHTML.ThemeSetting.Setting.general_Setting')}</span>
                    <Tooltip
                      preferredPosition="above"
                      content={
                        <div style={{ maxWidth: '15rem' }}>
                          {i18n.translate('AdminHTML.ThemeSetting.Tooltip.general_Setting')}
                        </div>
                      }>
                      <Icon source={QuestionMarkMinor} color="base" />
                    </Tooltip>
                  </div>
                </div>

                {menuCommonSetting.map(item => {
                  const MenuRendered = !_.isEmpty(item.subMenu) ? MenuItemHasSub : MenuItem;
                  return (
                    <MenuRendered
                      {...item}
                      key={item.id}
                      activeMenuItem={activeMenuItem}
                      updateType={TYPE_UPDATE_SETTING.COMMON}
                      handleChangeActiveMenuItem={this.handleChangeActiveMenuItem}
                      handleUpdateMenuSetting={this.handleUpdateMenuSetting}
                      upsertElementActions={upsertElementActions}
                      menus={flattenCommonMenu}
                      menuCollection={menuCollectionSetting}
                      updateField={updateField}
                      formValues={formValues}
                      themeSettingDefaultValue={themeSettingDefaultValue?.value}
                      themeSettingDefaultThemeValue={themeSettingDefaultThemeValue?.value}
                      themeSettingDefaultValueOriginal={themeSettingDefaultValueOriginal?.value}
                      themeName={themeName}
                      removeCurrentElement={removeCurrentElement}
                      updateFieldReducer={updateFieldReducer}
                    />
                  );
                })}
              </div>
            </form>
          </div>
          <div className="reset-wrapper">
            <Button
              textAlign="center"
              fullWidth
              icon={<IconSync />}
              onClick={() => this.handleToggleModalReset(true)}
              disabled={!isDifferentFormOrigin}>
              <span>{i18n.translate('AdminHTML.ThemeSetting.Common.resetSetting')}</span>
            </Button>
          </div>

          <ConfirmResetModal
            active={isShowModalReset}
            handleCancel={this.handleCancelReset}
            handleReset={this.handleReset}
          />
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (field, value) => dispatch(change(`ThemeSettingVisualEditorForm`, field, value)),
  changeActiveMenuItem: (item, updateType) =>
    dispatch(ThemeSettingAction.handleChangeActiveMenuItem(item, updateType)),
  handleUpdateMenuSetting: (id, children, updateType) =>
    dispatch(ThemeSettingAction.handleUpdateMenuSetting(id, children, updateType)),
  toggleHideMenuItem: (id, updateType) =>
    dispatch(ThemeSettingAction.handleToggleHideMenuItem(id, updateType)),
  updateThemeSetting: setting =>
    dispatch(ThemeSettingAction.updateThemeSetting(setting, ownProps.i18n)),
  updateFieldReducer: (fieldName, data) =>
    dispatch(ThemeSettingAction.handleUpdateField(fieldName, data)),
  updateMultiFieldsReducer: data => dispatch(ThemeSettingAction.handleUpdateMultiFields(data)),
  resetForm: () => dispatch(reset('ThemeSettingVisualEditorForm')),
  removeCurrentElement: (activeMenuItem, updateType) =>
    dispatch(ThemeSettingAction.handleRemoveElement(activeMenuItem, updateType))
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('ThemeSettingVisualEditorForm')(state) || {};
  const isExpandFullscreen = _.get(state, 'themeSetting.device') === DEVICE.FULLSCREEN;

  return {
    formValues,
    isExpandFullscreen,
    activeMenuItem: _.get(state, 'themeSetting.activeMenuItem', ''),
    selectedPage: _.get(state, 'themeSetting.selectedPage', ''),
    menuCommonSetting: _.get(state, 'themeSetting.commonSetting', []),
    menuCollectionSetting: _.get(state, 'themeSetting.collectionSetting', []),
    menuSearchSetting: _.get(state, 'themeSetting.searchSetting', []),
    themeSettingDefaultValue: _.get(state, 'themeSetting.themeSettingDefaultValue', {}),
    themeSettingDefaultThemeValue: _.get(state, 'themeSetting.themeSettingDefaultThemeValue', {}),
    themeSettingDefaultValueOriginal: _.get(
      state,
      'themeSetting.themeSettingDefaultValueOriginal',
      {}
    ),
    themeName: props?.themeName,
    sideBarMenu: _.get(state, 'themeSetting.sideBarMenu', ''),
    previewSetting: _.get(state, 'themeSetting.previewSetting', {})
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SettingsVisualEditor));
